import React, { useState, useEffect, useMemo } from 'react';
import ReadyToModelCard from './ReadyToModelCard';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import { ModelBuilderService } from '../services/modelBuilderService';
import { supabase } from '../lib/supabase';

const ReadyToModelCardView = ({ onUpdateProject, projects: allProjects }) => {
  const [eligibilityData, setEligibilityData] = useState({});
  const [modelBuilders, setModelBuilders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter for Ready to Model projects from shared state
  const readyToModelProjects = useMemo(() => {
    if (!allProjects || allProjects.length === 0) return [];
    return allProjects.filter(p => p.modelingStatus === 'Ready to Model');
  }, [allProjects]);

  useEffect(() => {
    loadEligibilityData();
  }, [readyToModelProjects]);

  const loadEligibilityData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (readyToModelProjects.length === 0) {
        setLoading(false);
        return;
      }

      const [buildersData, eligibilityResponse] = await Promise.all([
        ModelBuilderService.getModelBuilders(),
        supabase
          .from('project_model_builder_eligibility')
          .select(`
            *,
            employee:employees!employee_id(id, name)
          `)
          .in('project_id', readyToModelProjects.map(p => p.id))
      ]);

      setModelBuilders(buildersData);

      // Organize eligibility by project ID
      const eligibilityByProject = {};
      if (eligibilityResponse.data) {
        eligibilityResponse.data.forEach(e => {
          if (!eligibilityByProject[e.project_id]) {
            eligibilityByProject[e.project_id] = [];
          }
          eligibilityByProject[e.project_id].push(e);
        });
      }
      setEligibilityData(eligibilityByProject);
    } catch (err) {
      console.error('Error loading eligibility data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sortProjects = (projectsList) => {
    // Separate regular and paused projects
    const regularProjects = projectsList.filter(p => !p.isSoftHold);
    const pausedProjects = projectsList.filter(p => p.isSoftHold);

    // Sort each group by priority, then due date
    const sortGroup = (projects) => {
      return projects.sort((a, b) => {
        // High priority first
        const aPriority = a.isHighPriority ? 1 : 0;
        const bPriority = b.isHighPriority ? 1 : 0;
        if (aPriority !== bPriority) {
          return bPriority - aPriority;
        }

        // Then by due date (earliest first)
        const aDate = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bDate = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return aDate - bDate;
      });
    };

    // Combine: regular projects first, then paused at bottom
    return [...sortGroup(regularProjects), ...sortGroup(pausedProjects)];
  };

  const handleAssignmentChange = async (projectId) => {
    try {
      // Reload eligibility data when assignments change
      await loadEligibilityData();
    } catch (err) {
      console.error('Error reloading eligibility data:', err);
    }
  };

  // Combine projects with eligibility and sort
  const projectsWithEligibility = useMemo(() => {
    const combined = readyToModelProjects.map(project => ({
      ...project,
      eligibility: eligibilityData[project.id] || []
    }));
    return sortProjects(combined);
  }, [readyToModelProjects, eligibilityData]);

  // Show loading while projects are being loaded
  if (!allProjects || loading) {
    return (
      <div style={{ padding: 'var(--space-6)' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 'var(--space-6)' }}>
        <ErrorMessage message={error} />
      </div>
    );
  }

  if (projectsWithEligibility.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--space-8)',
          textAlign: 'center',
          color: 'var(--gray-400)',
        }}
      >
        <p style={{ fontSize: 'var(--text-lg)', margin: 0 }}>
          No projects are currently Ready to Model
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--space-6)' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 'var(--space-4)',
        }}
      >
        {projectsWithEligibility.map((project) => (
          <ReadyToModelCard
            key={project.id}
            project={project}
            modelBuilders={modelBuilders}
            onAssignmentChange={() => handleAssignmentChange(project.id)}
            onUpdateProject={onUpdateProject}
          />
        ))}
      </div>
    </div>
  );
};

export default ReadyToModelCardView;
